/**
 * Ambient audio for the table mice: short synthesized squeak chirps and the
 * faint skitter of feet, generated with WebAudio so no sample files are
 * needed. Everything here is set dressing — volumes are deliberately tiny.
 *
 * Browsers block audio until a user gesture, so the shared context is created
 * lazily on the first pointer/key interaction (the wake click qualifies);
 * every call before that is a silent no-op.
 */

const MASTER_GAIN = 0.055;
const MIN_SQUEAK_GAP_MS = 5000;
const SKITTER_MAX_S = 2.5;

/** Chance that starting a clip produces a squeak, per clip name. */
const SQUEAK_CHANCE: Record<string, number> = {
  sniff: 0.3,
  sit: 0.22,
  groom: 0.14,
  scurry: 0.12,
  idle: 0.05,
};

let context: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
let unlockArmed = false;
let lastSqueakAt = -Infinity;

function unlock(): void {
  if (context || !('AudioContext' in window)) return;
  context = new AudioContext({ latencyHint: 'playback' });
  master = context.createGain();
  master.gain.value = MASTER_GAIN;
  master.connect(context.destination);
  if (context.state !== 'running') void context.resume();
}

/** Arm the gesture unlock. Safe to call repeatedly; only the first arms it. */
export function initMouseAudio(): void {
  if (unlockArmed || typeof window === 'undefined') return;
  unlockArmed = true;
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true, passive: true });
}

/** A panned output node into the master bus, or null while audio is unavailable. */
function output(pan: number): { context: AudioContext; dest: AudioNode } | null {
  if (!context || !master || context.state !== 'running' || document.hidden) return null;
  const panner = context.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(master);
  return { context, dest: panner };
}

/** One frequency-modulated sine chirp — the body of a mouse squeak. */
function chirp(ctx: AudioContext, dest: AudioNode, at: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const startHz = 3800 + Math.random() * 2200;
  const lengthS = 0.05 + Math.random() * 0.07;
  osc.type = 'sine';
  osc.frequency.setValueAtTime(startHz, at);
  osc.frequency.exponentialRampToValueAtTime(startHz * (1.35 + Math.random() * 0.4), at + lengthS * 0.4);
  osc.frequency.exponentialRampToValueAtTime(startHz * 0.75, at + lengthS);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(0.9, at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, at + lengthS);
  osc.connect(gain).connect(dest);
  osc.start(at);
  osc.stop(at + lengthS + 0.02);
}

function squeak(pan: number): void {
  const out = output(pan);
  if (!out) return;
  const t0 = out.context.currentTime + 0.02;
  const chirps = 1 + Math.floor(Math.random() * 3);
  let at = t0;
  for (let i = 0; i < chirps; i += 1) {
    chirp(out.context, out.dest, at);
    at += 0.09 + Math.random() * 0.07;
  }
}

function ensureNoise(ctx: AudioContext): AudioBuffer {
  if (!noiseBuffer) {
    noiseBuffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.04), ctx.sampleRate);
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) samples[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

/** Faint claw-tick noise bursts for the length of a scurry. */
function skitter(durationMs: number, pan: number): void {
  const out = output(pan);
  if (!out) return;
  const noise = ensureNoise(out.context);
  const t0 = out.context.currentTime + 0.02;
  const seconds = Math.min(durationMs / 1000, SKITTER_MAX_S);
  for (let t = 0; t < seconds; t += 0.055 + Math.random() * 0.03) {
    const src = out.context.createBufferSource();
    const filter = out.context.createBiquadFilter();
    const gain = out.context.createGain();
    src.buffer = noise;
    filter.type = 'bandpass';
    filter.frequency.value = 2600 + Math.random() * 900;
    filter.Q.value = 2.2;
    gain.gain.value = 0.16 + Math.random() * 0.1;
    src.connect(filter).connect(gain).connect(out.dest);
    src.start(t0 + t);
  }
}

/** One deliberate squeak from the mouse table, used to cue an idle clip. */
export function cueSqueak(): void {
  lastSqueakAt = performance.now();
  squeak(-0.4);
}

/**
 * Play whatever a clip transition sounds like: skitter for the length of a
 * scurry, plus an occasional squeak (rate-limited across all mice).
 * `x` is the mouse's 0..1 position on the tabletop, used only for panning —
 * the table sits left of centre stage.
 */
export function clipAudio(clip: string, x: number, moveMs: number): void {
  if (clip === 'scurry') skitter(moveMs, -0.5 + x * 0.3);
  const now = performance.now();
  if (now - lastSqueakAt < MIN_SQUEAK_GAP_MS) return;
  if (Math.random() < (SQUEAK_CHANCE[clip] ?? 0)) {
    lastSqueakAt = now;
    squeak(-0.5 + x * 0.3);
  }
}
